import { ReactComponent as UploadIcon } from "../../../../assets/Icon-Upload-White.svg";
import { Box, Button, Grid, Typography } from "@mui/material";
import CSVReader, { IFileInfo } from "react-csv-reader";
import { useEffect, useRef, useState } from "react";
import { gradeOptions } from "./GradeSelect";
import { z } from "zod";
import { makeStyles } from "@mui/styles";
import { generateGtmEvent } from "../../../../Helpers";

const csvSchema = z
  .object({
    first_name: z.string().min(1),
    last_name: z.string().min(1),
    email: z.string().email(),
    grade: z
      .preprocess((v) => (v as string).trim(), z.enum([...gradeOptions, ""]))
      .transform((v) => (v === "N/A" || v === "" ? undefined : Number(v))),
  })
  .array()
  .min(1, "The CSV file must contain at least 1 row of users.");

export type BulkUsersArray = z.infer<typeof csvSchema>;

type BulkStudentsFileInputProps = {
  onChange: (users: BulkUsersArray, fileName: string) => void;
  onError: (message: string) => void;
};

const useStyles = makeStyles({
  csvReader: {
    display: "none",
  },
});

export function BulkStudentsFileInput(props: BulkStudentsFileInputProps) {
  const classes = useStyles();
  const csvReaderRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState<string>("No file chosen");

  const HandleDataLayer = (
    event: string,
    action: string,
    name: string,
    type: string,
    region: string,
    section: string,
    text: string,
    metaData?: any
  ) => {
    generateGtmEvent({ event, action, name, type, region, section, text }, metaData);
  };

  useEffect(() => {
    const csvInput = document.getElementById(
      "csv-reader-input"
    ) as HTMLInputElement | null;
    csvReaderRef.current = csvInput;
  }, []);

  const handleLoad = (data: any[], fileInfo: IFileInfo) => {
    setFileName(fileInfo.name);
    const result = csvSchema.safeParse(data);
    if (result.success) {
      props.onChange(result.data, fileInfo.name);
    } else {
      props.onError(
        `The uploaded CSV file is not formatted correctly: ${JSON.stringify(
          result.error.issues[0],
          null,
          2
        )}`
      );
    }
  };

  const handleButtonClick = () => {
    /**
     * Styling the CSVReader component
     * is hard so we're hiding it and
     * triggering a click when the fake button
     * is pressed.
     */
    csvReaderRef.current?.click();
    HandleDataLayer(
      "link",
      "click",
      "onclick",
      "internal link",
      "modal",
      "add bulk users",
      "choose file"
    );
  };

  return (
    <>
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        component="span"
        spacing={2}>
        <Grid item zeroMinWidth>
          <Button
            onClick={handleButtonClick}
            variant="contained"
            size="large"
            sx={{ height: 40 }}
            disableElevation>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <UploadIcon width={16} />
              </Grid>
              <Grid item>Choose File</Grid>
            </Grid>
          </Button>
        </Grid>
        <Grid item component={Box} color="#5D5D5D">
          <Typography variant="body2" color="inherit">
            {fileName}
          </Typography>
        </Grid>
      </Grid>
      <CSVReader
        inputId="csv-reader-input"
        cssClass={classes.csvReader}
        onFileLoaded={handleLoad}
        parserOptions={{
          header: true,
          skipEmptyLines: true,
          transformHeader: (header: string) => header.toLowerCase().replace(/\W/g, "_"),
        }}
      />
    </>
  );
}
